import { supabase } from "@/lib/supabase";

// ---------------------------------------------------------------------------
// R2 Configuration — only non-secret variables stay in the frontend bundle
// ---------------------------------------------------------------------------

const R2_PUBLIC_URL = (import.meta.env.VITE_R2_PUBLIC_URL as string | undefined)?.replace(/\/$/, "") ?? "";
const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? "";

const hasFunctionsConfig = Boolean(SUPABASE_URL);
const hasPublicUrlConfig = Boolean(R2_PUBLIC_URL);

// Edge Function URL — credentials never leave the server
const PRESIGN_FN_URL = `${SUPABASE_URL}/functions/v1/r2-presigned`;

// ---------------------------------------------------------------------------
// Image optimisation presets
// ---------------------------------------------------------------------------

export type ImagePreset = "avatar" | "banner" | "cover" | "thumbnail";

const PRESET_CONFIG: Record<ImagePreset, { maxWidth: number; quality: number }> = {
  avatar: { maxWidth: 400, quality: 0.9 },
  banner: { maxWidth: 1920, quality: 0.85 },
  cover: { maxWidth: 1920, quality: 0.85 },
  thumbnail: { maxWidth: 800, quality: 0.85 },
};

// ---------------------------------------------------------------------------
// optimizeImage — Canvas-based resize + WebP conversion
// ---------------------------------------------------------------------------

export async function optimizeImage(
  file: File,
  preset: ImagePreset = "banner"
): Promise<File> {
  if (!file.type.startsWith("image/")) return file;

  const { maxWidth, quality } = PRESET_CONFIG[preset];

  const bitmap = await createImageBitmap(file);
  let { width, height } = bitmap;

  if (width > maxWidth) {
    const ratio = maxWidth / width;
    width = maxWidth;
    height = Math.round(height * ratio);
  }

  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await canvas.convertToBlob({ type: "image/webp", quality });
  const optimisedName = file.name.replace(/\.[^.]+$/, ".webp");
  return new File([blob], optimisedName, { type: "image/webp" });
}

// ---------------------------------------------------------------------------
// getAuthHeader — retrieve the current session JWT for Edge Function calls
// ---------------------------------------------------------------------------

async function getAuthHeader(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("Sessão não encontrada. Faça login novamente.");
  return `Bearer ${session.access_token}`;
}

// ---------------------------------------------------------------------------
// requestR2UploadUrl — ask the Edge Function for a signed upload URL
// ---------------------------------------------------------------------------

function deriveLegacyUploadFields(key: string): { folder?: string; fileName?: string } {
  const normalizedKey = key.trim().replace(/^\/+|\/+$/g, "");
  if (!normalizedKey) return {};

  const segments = normalizedKey.split("/").filter(Boolean);
  const fileName = segments.pop();
  const folder = segments.join("/");

  return {
    folder: folder || undefined,
    fileName: fileName || undefined,
  };
}

export async function requestR2UploadUrl(payload: {
  key: string;
  contentType: string;
}): Promise<{ uploadUrl: string; publicUrl: string }> {
  if (!hasFunctionsConfig) {
    throw new Error("Supabase não configurado para upload no ambiente atual.");
  }

  const authHeader = await getAuthHeader();
  const legacyFields = deriveLegacyUploadFields(payload.key);
  const res = await fetch(PRESIGN_FN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: authHeader },
    body: JSON.stringify({
      action: "upload",
      ...payload,
      ...legacyFields,
    }),
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => res.statusText);
    throw new Error(`Falha ao obter presigned URL: ${msg}`);
  }

  const data = await res.json() as {
    uploadUrl?: string;
    presignedUrl?: string;
    publicUrl?: string;
  };

  const uploadUrl = data.uploadUrl ?? data.presignedUrl;

  if (!uploadUrl || !data.publicUrl) {
    throw new Error("Resposta inválida da Edge Function de upload.");
  }

  return {
    uploadUrl,
    publicUrl: data.publicUrl,
  };
}

// ---------------------------------------------------------------------------
// putFileToPresignedUrl — upload the final file to the signed URL
// ---------------------------------------------------------------------------

export async function putFileToPresignedUrl(
  uploadUrl: string,
  file: File
): Promise<Response> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      "Cache-Control": "public, max-age=31536000, immutable",
    },
  });

  if (!response.ok) {
    throw new Error(`Falha no upload para R2: ${response.status}`);
  }

  return response;
}

// ---------------------------------------------------------------------------
// deleteFromR2 — silently remove a file by its public URL
// ---------------------------------------------------------------------------

export async function deleteFromR2(url: string): Promise<void> {
  if (!hasFunctionsConfig || !hasPublicUrlConfig || !url || !url.startsWith(R2_PUBLIC_URL)) return;

  // Strip query string and fragment before extracting the object key
  // (cache-busting params like ?v=123 would break key matching otherwise).
  const cleanUrl = url.split("?")[0].split("#")[0];
  const key = cleanUrl.replace(`${R2_PUBLIC_URL}/`, "");
  if (!key) return;

  try {
    const authHeader = await getAuthHeader();
    await fetch(PRESIGN_FN_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: authHeader },
      body: JSON.stringify({ action: "delete", key }),
    });
  } catch {
    console.warn("[R2] Falha ao deletar:", key);
  }
}

// ---------------------------------------------------------------------------
// isR2Url — helper to check if a URL belongs to our R2 bucket
// ---------------------------------------------------------------------------

export function isR2Url(url: string): boolean {
  return hasPublicUrlConfig && !!url && url.startsWith(R2_PUBLIC_URL);
}

// ---------------------------------------------------------------------------
// fetchR2AsDataUrl — fetch an R2 image via Edge Function proxy and return as
// data URL.  The proxy bypasses R2 CORS restrictions by fetching server-side.
// Used by certificate download (html2canvas).
// ---------------------------------------------------------------------------

export async function fetchR2AsDataUrl(publicUrl: string): Promise<string> {
  if (!isR2Url(publicUrl)) throw new Error("Not an R2 URL");

  // Extract the object key from the public URL
  const key = publicUrl.replace(R2_PUBLIC_URL + "/", "");

  // Get current session token for Edge Function auth
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("Usuário não autenticado");

  // Fetch through Edge Function proxy (server-side, no CORS issues)
  const response = await fetch(PRESIGN_FN_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ action: "proxy", key }),
  });

  if (!response.ok) throw new Error(`Falha ao buscar imagem R2: ${response.status}`);

  const blob = await response.blob();

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
